import { useState, useEffect, useRef } from 'react';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import { useToast } from '../ui/Toast';
import { formatDate } from '../../utils/format';
import SowSelect from '../shared/SowSelect';
import Modal from '../shared/Modal';
import ContractDaysBreakdown from './ContractDaysBreakdown';


// Lazy-loaded client contacts for a SOW's client — used in the edit contract form
function ClientContactDropdown({ sowId, value, onChange, placeholder = '— select client contact —' }) {
  const [contacts, setContacts] = useState([]);
  useEffect(() => {
    if (!sowId) return;
    api.get(`sows/${sowId}/resource-contract-info`).then(info => setContacts(info.client_contacts || [])).catch(() => {});
  }, [sowId]);
  return (
    <select className="form-input text-[10px]" value={value} onChange={e => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {contacts.map(cc => <option key={cc.id} value={cc.id}>{cc.name}{cc.email ? ` (${cc.email})` : ''}</option>)}
    </select>
  );
}

function fmtDT(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const TS_BADGE = { draft:'bg-gray-100 text-gray-600', submitted:'bg-blue-100 text-blue-700', approved:'bg-green-100 text-green-700', rejected:'bg-red-100 text-red-700' };

// One contractor's whole record: details, contracts, onboarding, timesheets,
// access log and comments, with the dialogs that change them. It used to be
// the right-hand half of the External Resources page; it now opens in the
// Contractor Dashboard's side drawer, so the list it sits beside is that
// table. `workerId` is the contractor shown; `onChanged` tells the dashboard
// to reload its rows after anything that shows there (status, supplier,
// contracts), and `onDeleted` closes the drawer once the record is gone.
// `initialTab` opens somewhere other than Info: a contractor who was added a
// moment ago opens on Contracts, since a contract is what they need next.
export default function ContractorPanel({ workerId, initialTab = 'info', suppliers: allSuppliers, onSuppliersChanged, onChanged, onDeleted, onClose }) {
  const { toast } = useToast();
  const { navigate } = useApp() || {};
  const selected = workerId;
  const [sows, setSows]           = useState([]);
  const [detailTab, setDetailTab]     = useState('info');
  const [contractsSubTab, setContractsSubTab] = useState('active');

  const [detail, setDetail]           = useState(null);
  const [timesheets, setTimesheets]   = useState([]);
  const [workerSows, setWorkerSows]   = useState([]);
  const [accessLog, setAccessLog]     = useState([]);
  const [onboarding, setOnboarding]   = useState([]);
  const [comments, setComments]       = useState([]);
  const [commentForm, setCommentForm] = useState({ body: '', contract_id: '' });
  const [commentBusy, setCommentBusy] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  // Delete
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting]     = useState(false);

  // Edit
  const [showEdit, setShowEdit]   = useState(false);
  const [editForm, setEditForm]   = useState({});
  const [editError, setEditError] = useState('');
  const [editing, setEditing]     = useState(false);

  // Password reset
  const [showPwReset, setShowPwReset] = useState(false);
  const [newPw, setNewPw]             = useState('');
  const [showWelcome, setShowWelcome] = useState(false);
  const [sendingWelcome, setSendingWelcome] = useState(false);

  const BLANK_SOW_FORM = { sow_id:'', role_description:'', notes:'', day_rate:'', agreed_days:'', agreed_expenses:'', start_date:'', end_date:'', is_primary:false, status:'draft', overhead_category:'', timesheet_approver_type:'external', timesheet_approver_id:'', delegate_approver_type:'external', delegate_approver_id:'', reference:'' };

  // Add Contract
  const [showAddSow, setShowAddSow] = useState(false);
  const [sowForm, setSowForm]       = useState(BLANK_SOW_FORM);
  const [sowError, setSowError]     = useState('');
  const [sowInfo, setSowInfo]       = useState(null); // { default_days, default_day_rate, existing_contracts, client_contacts } for selected SOW
  const [internalPeople, setInternalPeople] = useState([]);

  // Load internal people for approver dropdown (once)
  useEffect(() => {
    api.get('people?limit=200').then(r => setInternalPeople((r.people || r || []).filter(p => p.email_work))).catch(() => {});
  }, []);

  // When a SOW is picked, default the days/rate from it (1:1 SOW↔contract) and
  // surface a warning if it already backs another contract.
  const onSelectSow = async (sowId) => {
    setSowForm(f => ({ ...f, sow_id: sowId, overhead_category: '' }));
    setSowInfo(null);
    if (!sowId) return;
    try {
      const info = await api.get(`sows/${sowId}/resource-contract-info`);
      setSowInfo(info);
      setSowForm(f => ({
        ...f,
        agreed_days:            (f.agreed_days === '' && info.default_days != null)     ? String(info.default_days)     : f.agreed_days,
        day_rate:               (f.day_rate === '' && info.default_day_rate != null)    ? String(info.default_day_rate) : f.day_rate,
        timesheet_approver_type: f.timesheet_approver_id === '' ? 'external' : f.timesheet_approver_type,
        timesheet_approver_id:  (f.timesheet_approver_id === '' && info.default_approver_id) ? info.default_approver_id : f.timesheet_approver_id,
      }));
    } catch { /* non-fatal */ }
  };

  // Edit Contract
  const [editingSow, setEditingSow] = useState(null);
  const [editSowForm, setEditSowForm] = useState({});
  const [editSowError, setEditSowError] = useState('');

  // PDF contract document upload
  const [uploadingDoc, setUploadingDoc] = useState(null);
  // A refused contract upload says why beside the contract it was for.
  const [docError, setDocError] = useState(null); // { contractId, message }
  const docInputRef = useRef(null);

  const [approvingTs, setApprovingTs] = useState(null);

  // Link / create a supplier for this contractor
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierCreateMode, setSupplierCreateMode] = useState(false);
  const [supplierForm, setSupplierForm] = useState({ name: '', company_number: '', vat_number: '', address: '', contact_name: '', contact_email: '', contact_phone: '' });
  const [supplierSaving, setSupplierSaving] = useState(false);
  const [supplierMsg, setSupplierMsg] = useState('');

  // Every load carries a token so a slow response for a worker we've since
  // navigated away from can't overwrite the current one. Without this, `detail`
  // could end up describing a different resource to the one in `selected` —
  // and the edit modal would then save one person's details onto another.
  const detailReq = useRef(0);

  const loadDetail = (id) => {
    const req = ++detailReq.current;
    setDetailLoading(true);
    Promise.all([
      api.get(`resource-workers/${id}`),
      api.get(`resource-workers/${id}/timesheets`),
      api.get(`resource-workers/${id}/sows`),
      api.get(`resource-workers/${id}/access-log`),
      api.get(`resource-workers/${id}/comments`).catch(() => []),
    ]).then(([d, ts, ws, log, cm]) => {
      if (req !== detailReq.current) return;
      setDetail(d);
      setTimesheets(ts || []);
      setWorkerSows(ws || []);
      setAccessLog(log || []);
      setComments(cm || []);
      setDetailLoading(false);
    }).catch(() => { if (req === detailReq.current) setDetailLoading(false); });
    loadOnboarding(id, req);
  };

  const loadComments = (id) => api.get(`resource-workers/${id}/comments`).then(c => setComments(c || [])).catch(() => {});
  const addComment = async () => {
    const text = commentForm.body.trim();
    if (!text) return;
    setCommentBusy(true);
    try {
      await api.post(`resource-workers/${selected}/comments`, { body: text, contract_id: commentForm.contract_id || null });
      setCommentForm({ body: '', contract_id: '' });
      await loadComments(selected);
    } catch (e) { toast.error(e.message || 'Could not add comment'); }
    setCommentBusy(false);
  };
  const deleteComment = async (cid) => {
    if (!window.confirm('Delete this comment?')) return;
    try { await api.delete(`resource-workers/${selected}/comments/${cid}`); await loadComments(selected); }
    catch (e) { toast.error(e.message || 'Could not delete'); }
  };

  const loadOnboarding = (id, req = null) => {
    api.get(`resource-workers/${id}/onboarding`)
      .then(r => { if (req === null || req === detailReq.current) setOnboarding(r || []); })
      .catch(() => { if (req === null || req === detailReq.current) setOnboarding([]); });
  };
  const setOnboardingActivity = async (activityId, patch) => {
    try {
      const res = await api.post(`resource-workers/${selected}/onboarding/${activityId}`, patch);
      if (res?.warning) toast.warning ? toast.warning(res.warning) : toast.error(res.warning);
      loadOnboarding(selected);
    } catch (e) { toast.error(e.message || 'Could not update onboarding'); }
  };

  // The SOW list backs the contract dialogs' SOW pickers. The panel stays
  // mounted while the drawer moves from one contractor to another, so it is
  // fetched once rather than per contractor.
  useEffect(() => {
    api.get('sows').then(s => setSows(s || [])).catch(() => {});
  }, []);

  // A different contractor: drop the outgoing one's data rather than leaving
  // it on screen while the new one loads, since the header and the edit modal
  // both read `detail`, and start again on the tab asked for.
  useEffect(() => {
    if (!workerId) return;
    setDetail(null);
    setTimesheets([]);
    setWorkerSows([]);
    setAccessLog([]);
    setComments([]);
    setOnboarding([]);
    setDetailTab(initialTab);
    setContractsSubTab('active');
    loadDetail(workerId);
  }, [workerId, initialTab]); // eslint-disable-line

  const openEdit = () => {
    if (!detail || detail.id !== selected) return;
    setEditForm({
      id: detail.id,
      first_name: detail.first_name || '',
      last_name: detail.last_name || '',
      email: detail.email || '',
      phone: detail.phone || '',
      notes: detail.notes || '',
      emails_enabled: detail.emails_enabled !== undefined ? !!detail.emails_enabled : true,
    });
    setEditError('');
    setShowEdit(true);
  };

  // Supplier-edit mini-modal
  const [showEditSupplierModal, setShowEditSupplierModal] = useState(false);
  const [editSupplierForm, setEditSupplierForm] = useState({});
  const [editSupplierSaving, setEditSupplierSaving] = useState(false);
  const [editSupplierMsg, setEditSupplierMsg] = useState('');

  const openEditSupplier = () => {
    const sup = allSuppliers.find(s => s.id === detail?.supplier_id);
    if (!sup) return;
    setEditSupplierForm({
      name: sup.name || '',
      company_number: sup.company_number || '',
      vat_number: sup.vat_number || '',
      address: sup.address || '',
      contact_name: sup.contact_name || '',
      contact_email: sup.contact_email || '',
      contact_phone: sup.contact_phone || '',
    });
    setEditSupplierMsg('');
    setShowEdit(false);
    setShowEditSupplierModal(true);
  };

  const saveEditSupplier = async () => {
    if (!detail?.supplier_id) return;
    setEditSupplierSaving(true);
    setEditSupplierMsg('');
    try {
      await api.put(`suppliers/${detail.supplier_id}`, editSupplierForm);
      const fresh = await api.get('suppliers');
      onSuppliersChanged?.(fresh || []);
      const updated = (fresh || []).find(s => s.id === detail.supplier_id);
      if (updated) setDetail(d => ({ ...d, supplier_name: updated.name }));
      setShowEditSupplierModal(false);
    } catch (e) {
      setEditSupplierMsg(e.message || 'Failed to save supplier.');
    } finally {
      setEditSupplierSaving(false);
    }
  };

  const saveEdit = async () => {
    setEditError('');
    if (!editForm.first_name || !editForm.last_name) { setEditError('First and last name are required.'); return; }
    // Save against the worker the form was opened for, never whatever happens
    // to be selected by the time the modal is submitted.
    const workerId = editForm.id;
    if (!workerId) { setEditError('This form is out of date. Close it and try again.'); return; }
    setEditing(true);
    try {
      const { id: _id, ...form } = editForm;
      const payload = { ...form, emails_enabled: editForm.emails_enabled ? 1 : 0 };
      if (editForm.email) {
        payload.email = editForm.email.trim();
        payload.username = payload.email.toLowerCase();
      }
      await api.put(`resource-workers/${workerId}`, payload);
      setShowEdit(false);
      loadDetail(selected);
      onChanged?.();
    } catch (err) {
      setEditError(err.message || 'Failed to update');
    } finally {
      setEditing(false);
    }
  };

  const deleteWorker = async () => {
    setDeleting(true);
    try {
      await api.delete(`resource-workers/${selected}`);
      setShowDelete(false);
      setDetail(null);
      onDeleted?.();
    } catch (err) {
      toast.error(err.message || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const toggleActive = async (w) => {
    await api.put(`resource-workers/${w.id}`, { is_active: w.is_active ? 0 : 1 });
    onChanged?.();
    if (selected === w.id) loadDetail(w.id);
  };

  const resetPassword = async () => {
    if (!newPw) return;
    await api.put(`resource-workers/${selected}`, { new_password: newPw });
    setShowPwReset(false);
    setNewPw('');
    toast.success('Password reset. Account unlocked and all sessions invalidated.');
    loadDetail(selected);
  };

  const sendWelcome = async () => {
    setSendingWelcome(true);
    try {
      const res = await api.post(`resource-workers/${selected}/welcome`, {});
      if (res?.email_sent) {
        toast.success('Welcome email sent. A temporary password was set — they must change it on first login.');
      } else {
        toast.success('Password reset, but the welcome email could not be sent. Check the portal mailbox configuration.');
      }
      setShowWelcome(false);
      loadDetail(selected);
    } catch (err) {
      toast.error(err.message || 'Failed to send welcome email');
    } finally {
      setSendingWelcome(false);
    }
  };

  const unlockAccount = async () => {
    await api.put(`resource-workers/${selected}`, { unlock: true });
    toast.success('Account unlocked. They can sign in again.');
    onChanged?.();
    loadDetail(selected);
  };

  const addSow = async () => {
    if (!sowForm.sow_id && !sowForm.overhead_category) {
      setSowError('Select a SOW or choose an overhead category.');
      return;
    }
    setSowError('');
    try {
      await api.post(`resource-workers/${selected}/contracts`, {
        sow_id:                   sowForm.sow_id || null,
        reference:                sowForm.reference?.trim() || null,
        role_description:         sowForm.role_description || null,
        notes:                    sowForm.notes || null,
        day_rate:                 sowForm.day_rate ? parseFloat(sowForm.day_rate) : null,
        agreed_days:              sowForm.agreed_days ? parseFloat(sowForm.agreed_days) : null,
        agreed_expenses:          sowForm.agreed_expenses ? parseFloat(sowForm.agreed_expenses) : null,
        start_date:               sowForm.start_date || null,
        end_date:                 sowForm.end_date || null,
        is_primary:               sowForm.is_primary ? 1 : 0,
        status:                   sowForm.status || 'draft',
        overhead_category:        sowForm.sow_id ? null : (sowForm.overhead_category || null),
        timesheet_approver_type:  sowForm.timesheet_approver_id ? sowForm.timesheet_approver_type : null,
        timesheet_approver_id:    sowForm.timesheet_approver_id || null,
        delegate_approver_type:   sowForm.delegate_approver_id ? sowForm.delegate_approver_type : null,
        delegate_approver_id:     sowForm.delegate_approver_id || null,
      });
      setShowAddSow(false);
      setSowForm(BLANK_SOW_FORM);
      setSowInfo(null);
      loadDetail(selected);
      onChanged?.();
    } catch (err) {
      setSowError(err.message || 'Failed to create contract');
    }
  };

  const openEditSow = (s) => {
    setEditingSow(s);
    setEditSowForm({
      role_description:        s.role_description || '',
      day_rate:                s.day_rate != null ? String(s.day_rate) : '',
      agreed_days:             s.agreed_days != null ? String(s.agreed_days) : '',
      agreed_expenses:         s.agreed_expenses != null ? String(s.agreed_expenses) : '',
      start_date:              s.start_date || '',
      end_date:                s.end_date || '',
      is_primary:              !!s.is_primary,
      notes:                   s.notes || '',
      status:                  s.status || 'draft',
      overhead_category:       s.overhead_category || '',
      sow_id:                  s.sow_id || '',
      timesheet_approver_type: s.timesheet_approver_type || 'external',
      timesheet_approver_id:   s.timesheet_approver_id || '',
      delegate_approver_type:  s.delegate_approver_type || 'external',
      delegate_approver_id:    s.delegate_approver_id || '',
      opening_days:            s.opening_days != null ? String(s.opening_days) : '',
      opening_expenses:        s.opening_expenses != null ? String(s.opening_expenses) : '',
      is_legacy:               !!s.is_legacy,
    });
    setEditSowError('');
  };

  const saveEditSow = async () => {
    setEditSowError('');
    try {
      await api.put(`resource-worker-sows/${editingSow.id}`, {
        role_description:        editSowForm.role_description || null,
        day_rate:                editSowForm.day_rate ? parseFloat(editSowForm.day_rate) : null,
        agreed_days:             editSowForm.agreed_days ? parseFloat(editSowForm.agreed_days) : null,
        agreed_expenses:         editSowForm.agreed_expenses ? parseFloat(editSowForm.agreed_expenses) : null,
        start_date:              editSowForm.start_date || null,
        end_date:                editSowForm.end_date || null,
        is_primary:              editSowForm.is_primary ? 1 : 0,
        notes:                   editSowForm.notes || null,
        status:                  editSowForm.status || null,
        overhead_category:       editSowForm.sow_id ? null : (editSowForm.overhead_category || null),
        sow_id:                  editSowForm.sow_id || null,
        timesheet_approver_type: editSowForm.timesheet_approver_id ? editSowForm.timesheet_approver_type : null,
        timesheet_approver_id:   editSowForm.timesheet_approver_id || null,
        delegate_approver_type:  editSowForm.delegate_approver_id ? editSowForm.delegate_approver_type : null,
        delegate_approver_id:    editSowForm.delegate_approver_id || null,
        opening_days:            editSowForm.opening_days !== '' ? parseFloat(editSowForm.opening_days) : 0,
        opening_expenses:        editSowForm.opening_expenses !== '' ? parseFloat(editSowForm.opening_expenses) : 0,
        is_legacy:               editSowForm.is_legacy ? 1 : 0,
      });
      setEditingSow(null);
      loadDetail(selected);
    } catch (err) {
      setEditSowError(err.message || 'Failed to update contract');
    }
  };

  const removeSow = async (contractId) => {
    if (!window.confirm('Remove this contract?')) return;
    await api.delete(`resource-worker-sows/${contractId}`);
    loadDetail(selected);
    onChanged?.();
  };

  const uploadContractDoc = (contractId, file) => {
    if (!file) return;
    setUploadingDoc(contractId);
    setDocError(null);
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const bytes = new Uint8Array(e.target.result);
        let binary = '';
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        const base64 = btoa(binary);
        await api.put(`resource-worker-sows/${contractId}/document`, {
          filename: file.name,
          data: base64,
          content_type: file.type || 'application/pdf',
        });
        loadDetail(selected);
      } catch (err) {
        setDocError({ contractId, message: err.message || 'Upload failed' });
      } finally {
        setUploadingDoc(null);
      }
    };
    reader.onerror = () => { setDocError({ contractId, message: 'The file could not be read. Try choosing it again.' }); setUploadingDoc(null); };
    reader.readAsArrayBuffer(file);
  };

  const removeContractDoc = async (contractId) => {
    if (!window.confirm('Remove the uploaded contract document?')) return;
    await api.put(`resource-worker-sows/${contractId}/document`, { remove: true });
    loadDetail(selected);
  };

  function openSupplierModal(worker) {
    setSupplierSearch('');
    setSupplierCreateMode(false);
    setSupplierMsg('');
    setSupplierForm({
      name:           worker.company_name   || '',
      company_number: worker.company_number || '',
      vat_number:     worker.vat_number     || '',
      address:        worker.company_address|| '',
      contact_name:   `${worker.first_name} ${worker.last_name}`.trim(),
      contact_email:  worker.email          || '',
      contact_phone:  worker.phone          || '',
    });
    setShowSupplierModal(true);
  }

  async function linkSupplier(supplierId) {
    if (!detail) return;
    setSupplierSaving(true);
    try {
      await api.put(`resource-workers/${detail.id}`, { supplier_id: supplierId });
      setShowSupplierModal(false);
      // Reloaded rather than patched from the supplier list, which is a
      // render behind when the supplier was created a moment ago.
      loadDetail(detail.id);
      onChanged?.();
    } catch (err) {
      setSupplierMsg(err.message || 'Failed to link supplier.');
    } finally {
      setSupplierSaving(false);
    }
  }

  async function createAndLinkSupplier() {
    if (!supplierForm.name.trim()) { setSupplierMsg('Supplier name is required.'); return; }
    setSupplierSaving(true);
    setSupplierMsg('');
    try {
      const created = await api.post('suppliers', { ...supplierForm, supplies_resources: 1 });
      const fresh = await api.get('suppliers');
      onSuppliersChanged?.(fresh || []);
      await linkSupplier(created.id);
    } catch (err) {
      setSupplierMsg(err.message || 'Failed to create supplier.');
      setSupplierSaving(false);
    }
  }


  const approveTs = async (tsId, status) => {
    setApprovingTs(tsId);
    const payload = { status };
    if (status === 'rejected') {
      const reason = prompt('Rejection reason:');
      if (!reason) { setApprovingTs(null); return; }
      payload.rejection_reason = reason;
    }
    await api.put(`resource-timesheets/${tsId}`, payload);
    setApprovingTs(null);
    loadDetail(selected);
  };

  return (
    <>
    <div className="bg-white flex flex-col h-full min-h-0">
      {/* Header: who, then what can be done to the record */}
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/80 flex-shrink-0">
        <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-bold text-navy">{detail?.first_name} {detail?.last_name}</span>
            {detail && !detail.is_active && <span className="text-[7px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-semibold">Inactive</span>}
            {detail?.locked_at && <span className="text-[7px] bg-red-600 text-white px-1.5 py-0.5 rounded font-semibold" title={`Blocked after failed sign-ins on ${fmtDT(detail.locked_at)}`}>🔒 Blocked</span>}
          </div>
          {detail?.email && <div className="text-[10px] text-gray-500 mt-0.5">{detail.email}</div>}
          <div className="flex gap-4 mt-1">
            <span className="text-[8px] text-gray-400">Added: <span className="font-semibold text-gray-600">{formatDate(detail?.created_at)}</span></span>
            <span className="text-[8px] text-gray-400">Last login: <span className={`font-semibold ${detail?.last_login_at ? 'text-green-600' : 'text-gray-400'}`}>{detail?.last_login_at ? fmtDT(detail.last_login_at) : 'Never'}</span></span>
          </div>
        </div>
        <button onClick={onClose} aria-label="Close"
          className="w-6 h-6 rounded-full border border-gray-200 bg-white text-gray-400 hover:bg-gray-50 flex items-center justify-center text-[10px] flex-shrink-0">✕</button>
        </div>
        <div className="flex gap-2 flex-wrap mt-2">
          <button onClick={openEdit} disabled={!detail || detail.id !== selected}
            className="text-[9px] px-2 py-1 bg-gold/10 text-gold hover:bg-gold/20 rounded font-semibold disabled:opacity-40 disabled:cursor-not-allowed">Edit</button>
          {detail?.locked_at && <button onClick={unlockAccount} className="text-[9px] px-2 py-1 bg-amber-500 text-amber-950 rounded font-semibold hover:bg-amber-600">🔓 Unlock</button>}
          {detail?.email && <button onClick={() => setShowWelcome(true)} className="text-[9px] px-2 py-1 bg-navy/10 text-navy rounded font-semibold hover:bg-navy/20">✉ Send Welcome</button>}
          <button onClick={() => setShowPwReset(true)} className="text-[9px] px-2 py-1 bg-gray-100 rounded hover:bg-gray-200">Reset Password</button>
          {detail && detail.id === selected && (
            <button onClick={() => toggleActive(detail)}
              className={`text-[9px] px-2 py-1 rounded ${detail.is_active ? 'bg-red-50 text-red-600 hover:bg-red-100' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}>
              {detail.is_active ? 'Deactivate' : 'Activate'}
            </button>
          )}
          {detail && detail.id === selected && !detail.is_active && (
            <button onClick={() => setShowDelete(true)}
              className="text-[9px] px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700">
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-100 flex-shrink-0 overflow-x-auto">
        {['info','contracts','onboarding','timesheets','access-log','comments'].map(t => (
          <button key={t} onClick={() => setDetailTab(t)}
            className={`px-3 py-2 text-[9px] font-semibold border-b-2 capitalize ${detailTab === t ? 'border-navy text-navy' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
            {t.replace('-',' ')}
            {t === 'timesheets' && timesheets.filter(ts => ts.status === 'submitted').length > 0 && (
              <span className="ml-1 bg-blue-600 text-white text-[7px] px-1 py-0.5 rounded-full">{timesheets.filter(ts => ts.status === 'submitted').length}</span>
            )}
            {t === 'onboarding' && onboarding.length > 0 && (
              <span className="ml-1 bg-gray-200 text-gray-600 text-[7px] px-1 py-0.5 rounded-full">{onboarding.filter(a => a.progress?.status === 'complete').length}/{onboarding.length}</span>
            )}
          </button>
        ))}
      </div>

      <div className="p-4 flex-1 min-h-0 overflow-y-auto">
        {detailLoading && <div className="text-center py-8 text-gray-400 text-xs">Loading…</div>}
        {!detailLoading && detail && (
          <>
            {detailTab === 'info' && (
              <div>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    ['First Name',    detail.first_name],
                    ['Last Name',     detail.last_name],
                    ['Email (login)', detail.email || '—'],
                    ['Phone',         detail.phone || '—'],
                  ].map(([k, v]) => (
                    <div key={k}>
                      <div className="text-[8px] font-semibold text-gray-400 uppercase">{k}</div>
                      <div className="text-[11px] text-navy">{v}</div>
                    </div>
                  ))}
                  {detail.notes && (
                    <div className="col-span-2">
                      <div className="text-[8px] font-semibold text-gray-400 uppercase">Notes</div>
                      <div className="text-[11px] text-navy">{detail.notes}</div>
                    </div>
                  )}
                </div>
                {/* The staff record for the same person (El's item 11,
                    option B). Read-only here: the link is made from the
                    People screen, where the clearance and team it carries
                    are maintained. Shown so that anybody looking at a
                    contractor's contracts and invoices can see the record
                    holding their clearance, rather than the two existing
                    side by side with nothing joining them. */}
                {detail.person_id && (
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wide mb-2">Staff Record</div>
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div>
                          <div className="text-[11px] font-semibold text-navy">
                            {detail.person_name || 'Linked person'}
                            {detail.person_employee_id && (
                              <span className="ml-1.5 text-[8px] font-mono text-gray-400">{detail.person_employee_id}</span>
                            )}
                          </div>
                          <div className="text-[9px] text-gray-500 mt-0.5">
                            {detail.person_team_name || 'No team'}
                            {detail.person_clearance && detail.person_clearance !== 'none'
                              ? ` · ${String(detail.person_clearance).toUpperCase().replace('_', ' ')}`
                              : ' · no clearance recorded'}
                            {detail.person_status && detail.person_status !== 'active' ? ` · ${detail.person_status}` : ''}
                          </div>
                        </div>
                        {navigate && (
                          <button onClick={() => navigate('people', detail.person_id)}
                            className="text-[8px] text-gold hover:underline font-semibold flex-shrink-0">
                            Open staff record →
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Supplier */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wide">Supplier</span>
                    <button onClick={() => openSupplierModal(detail)}
                      className="text-[8px] btn btn-secondary px-2 py-0.5">
                      {detail.supplier_id ? 'Change' : 'Link Supplier'}
                    </button>
                  </div>
                  {detail.supplier_id ? (
                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-[11px] font-semibold text-navy">
                          {detail.supplier_name || allSuppliers.find(s => s.id === detail.supplier_id)?.name || 'Linked Supplier'}
                        </div>
                        {navigate && (
                          <button
                            onClick={() => navigate('suppliers', detail?.supplier_id)}
                            className="text-[8px] text-gold hover:underline font-semibold flex-shrink-0">
                            Manage QA →
                          </button>
                        )}
                      </div>
                      {(() => {
                        const sup = allSuppliers.find(s => s.id === detail.supplier_id);
                        if (!sup) return null;
                        const qaS = sup.qa_expired > 0 ? 'expired' : sup.qa_verified >= (sup.qa_doc_count || 0) && sup.qa_doc_count > 0 ? 'ok' : sup.qa_doc_count > 0 ? 'partial' : 'none';
                        const qaCls = { ok: 'bg-green-100 text-green-700', expired: 'bg-red-100 text-red-600', partial: 'bg-amber-100 text-amber-700', none: 'bg-gray-100 text-gray-400' }[qaS];
                        const qaMsg = { ok: 'All QA documents verified', expired: 'QA documents expired — action required', partial: 'QA documents partially complete', none: 'No QA documents on file' }[qaS];
                        return (
                          <div className="mt-2 space-y-0.5">
                            <div className={`inline-flex items-center gap-1 text-[8px] font-semibold px-1.5 py-0.5 rounded ${qaCls}`}>
                              {qaMsg}
                              {sup.qa_doc_count > 0 && <span className="opacity-70">({sup.qa_verified}/{sup.qa_doc_count})</span>}
                            </div>
                            {sup.company_number && <div className="text-[9px] text-gray-500 mt-1">Co. No: {sup.company_number}</div>}
                            {sup.vat_number     && <div className="text-[9px] text-gray-500">VAT: {sup.vat_number}</div>}
                            {sup.address        && <div className="text-[9px] text-gray-400 mt-1 whitespace-pre-line">{sup.address}</div>}
                          </div>
                        );
                      })()}
                    </div>
                  ) : (
                    <div className="text-[9px] text-gray-400 italic">No supplier linked — this contractor is not yet associated with a supplier company.</div>
                  )}
                </div>
              </div>
            )}

            {detailTab === 'contracts' && (() => {
              const activeContracts  = workerSows.filter(s => !s.is_legacy && !['completed','cancelled'].includes(s.status));
              const historyContracts = workerSows.filter(s => s.is_legacy  ||  ['completed','cancelled'].includes(s.status));
              const visibleContracts = contractsSubTab === 'active' ? activeContracts : historyContracts;
              return (
              <div>
                <input type="file" accept="application/pdf" ref={docInputRef} className="hidden"
                  onChange={e => { if (docInputRef.current?._contractId) uploadContractDoc(docInputRef.current._contractId, e.target.files[0]); e.target.value = ''; }} />
                <div className="flex items-center justify-between mb-3">
                  <div className="flex rounded-lg border border-gray-200 overflow-hidden text-[9px]">
                    {[
                      { id: 'active',  label: 'Active', count: activeContracts.length },
                      { id: 'history', label: 'Completed / Legacy', count: historyContracts.length },
                    ].map(t => (
                      <button key={t.id} onClick={() => setContractsSubTab(t.id)}
                        className={`px-3 py-1.5 font-semibold transition-colors flex items-center gap-1.5 border-l border-gray-200 first:border-l-0 ${contractsSubTab === t.id ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                        {t.label}
                        <span className={`text-[7px] px-1 py-0.5 rounded-full font-bold ${contractsSubTab === t.id ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>{t.count}</span>
                      </button>
                    ))}
                  </div>
                  <button onClick={() => { setSowInfo(null); setSowError(''); setSowForm({ sow_id:'', role_description:'', notes:'', day_rate:'', agreed_days:'', start_date:'', end_date:'', is_primary:false, status:'draft', overhead_category:'' }); setShowAddSow(true); }} className="text-[9px] px-2 py-1 bg-navy text-white rounded">+ New Contract</button>
                </div>
                {workerSows.length === 0 && <p className="text-[10px] text-gray-400">No contracts yet.</p>}
                {workerSows.length > 0 && visibleContracts.length === 0 && (
                  <p className="text-[10px] text-gray-400">No {contractsSubTab === 'active' ? 'active' : 'completed or legacy'} contracts.</p>
                )}
                {visibleContracts.map(s => {
                  const CONTRACT_STATUS = { draft:'bg-gray-100 text-gray-500', issued:'bg-blue-100 text-blue-700', active:'bg-green-100 text-green-700', completed:'bg-purple-100 text-purple-600', cancelled:'bg-red-100 text-red-500' };
                  const totalCost    = s.day_rate != null && s.agreed_days != null ? s.day_rate * s.agreed_days : null;
                  const agreed       = parseFloat(s.agreed_days)  || 0;
                  const invoiced     = parseFloat(s.invoiced_days) || 0;
                  const pending      = parseFloat(s.pending_days)  || 0;
                  const approved     = parseFloat(s.approved_days) || 0;
                  const opening      = parseFloat(s.opening_days)  || 0;
                  // Approved days not yet billed = awaiting invoice. Invoicing is
                  // tracked separately, so clamp to avoid double-counting.
                  const awaitingInv  = Math.max(0, approved - invoiced);
                  const consumed     = opening + pending + invoiced + awaitingInv;
                  const remaining    = Math.max(0, agreed - consumed);
                  const showDrawdown = agreed > 0;
                  return (
                    <div key={s.id} className="border border-gray-200 rounded p-3 mb-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            {s.reference && <span className="text-[9px] font-mono font-bold text-navy bg-navy/5 px-1.5 py-0.5 rounded">{s.reference}</span>}
                            {s.status && <span className={`text-[8px] px-1.5 py-0.5 rounded font-semibold capitalize ${CONTRACT_STATUS[s.status] || 'bg-gray-100 text-gray-500'}`}>{s.status}</span>}
                            {s.is_primary ? <span className="text-[8px] text-blue-600 font-semibold">Primary</span> : null}
                            {s.is_legacy ? <span className="text-[8px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-semibold">Legacy</span> : null}
                          </div>
                          {s.sow_id ? (
                            <div className="text-[10px] font-semibold text-navy">{s.sow_reference || 'SOW'} <span className="font-normal text-gray-400">— {s.client_name || ''}</span></div>
                          ) : (
                            <div className="text-[10px] font-semibold text-amber-700">{s.is_legacy ? 'Legacy — SOW unknown' : `Overhead — ${s.overhead_category ? s.overhead_category.charAt(0).toUpperCase() + s.overhead_category.slice(1).replace('_',' ') : 'No SOW'}`}</div>
                          )}
                          {s.sow_detail && <div className="text-[9px] text-gray-400 truncate">{s.sow_detail}</div>}
                          {s.approver_name && (
                            <div className="text-[9px] text-gray-400 mt-0.5">
                              Approver: <span className="font-semibold text-gray-600">{s.approver_name}</span>
                              {s.timesheet_approver_type === 'internal' && <span className="ml-1 text-[7px] bg-gray-100 text-gray-500 px-1 py-0.5 rounded">internal</span>}
                            </div>
                          )}
                          {s.delegate_approver_name && (
                            <div className="text-[9px] text-gray-400 mt-0.5">
                              Delegate: <span className="font-semibold text-gray-600">{s.delegate_approver_name}</span>
                              {s.delegate_approver_type === 'internal' && <span className="ml-1 text-[7px] bg-gray-100 text-gray-500 px-1 py-0.5 rounded">internal</span>}
                            </div>
                          )}
                          <div className="text-[9px] text-gray-500 mt-1 flex flex-wrap gap-3">
                            {s.day_rate    != null && <span className="font-semibold text-navy">£{s.day_rate}/day</span>}
                            {s.agreed_days != null && <span>{s.agreed_days}d agreed</span>}
                            {totalCost     != null && <span className="text-gray-400">= £{totalCost.toLocaleString()}</span>}
                            {s.start_date  && <span>{formatDate(s.start_date)} – {s.end_date ? formatDate(s.end_date) : 'ongoing'}</span>}
                          </div>
                          {s.role_description && <div className="text-[9px] text-gray-400 mt-1 italic">{s.role_description}</div>}
                          {showDrawdown && (
                            <div className="mt-2">
                              <div className="flex items-center justify-between mb-0.5 text-[8px]">
                                <span className="text-gray-500 font-semibold">Days drawdown</span>
                                <span className="text-gray-400">{remaining.toFixed(1)} remaining of {agreed}</span>
                              </div>
                              <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden flex">
                                {opening > 0 && (
                                  <div className="h-2 bg-slate-400 flex-shrink-0" style={{ width: `${Math.min(100, (opening / agreed) * 100)}%` }} title={`${opening} pre-system (opening balance)`} />
                                )}
                                {invoiced > 0 && (
                                  <div className="h-2 bg-emerald-500 flex-shrink-0" style={{ width: `${Math.min(100, (invoiced / agreed) * 100)}%` }} title={`${invoiced} invoiced`} />
                                )}
                                {awaitingInv > 0 && (
                                  <div className="h-2 bg-sky-500 flex-shrink-0" style={{ width: `${Math.min(100, (awaitingInv / agreed) * 100)}%` }} title={`${awaitingInv} approved, awaiting invoice`} />
                                )}
                                {pending > 0 && (
                                  <div className="h-2 bg-amber-400 flex-shrink-0" style={{ width: `${Math.min(100, (pending / agreed) * 100)}%` }} title={`${pending} pending approval`} />
                                )}
                              </div>
                              <div className="flex gap-3 mt-1 text-[7px] text-gray-500 flex-wrap">
                                {opening  > 0 && <span><span className="inline-block w-1.5 h-1.5 rounded-full bg-slate-400 mr-0.5" />{opening} pre-system</span>}
                                {pending  > 0 && <span><span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 mr-0.5" />{pending} pending approval</span>}
                                {awaitingInv > 0 && <span><span className="inline-block w-1.5 h-1.5 rounded-full bg-sky-500 mr-0.5" />{awaitingInv} awaiting invoice</span>}
                                {invoiced > 0 && <span><span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500 mr-0.5" />{invoiced} invoiced</span>}
                                {remaining > 0 && <span><span className="inline-block w-1.5 h-1.5 rounded-full bg-gray-300 mr-0.5" />{remaining.toFixed(1)} remaining</span>}
                              </div>
                            </div>
                          )}
                          <ContractDaysBreakdown workerId={selected} contractId={s.id} />
                          {/* PDF document */}
                          <div className="mt-2 flex items-center gap-2 flex-wrap">
                            {s.contract_document_url ? (
                              <>
                                <a href={s.contract_document_url} target="_blank" rel="noreferrer"
                                  className="text-[8px] text-blue-600 hover:underline font-semibold truncate max-w-[160px]">
                                  {s.contract_document_name || 'Contract PDF'}
                                </a>
                                <button onClick={() => removeContractDoc(s.id)} className="text-[7px] text-red-400 hover:text-red-600">remove</button>
                              </>
                            ) : (
                              <>
                                {s.status === 'active' && !s.is_legacy && (
                                  <span className="text-[8px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded flex items-center gap-1">
                                    ⚠ No contract document
                                  </span>
                                )}
                                <button
                                  onClick={() => { if (docInputRef.current) { docInputRef.current._contractId = s.id; docInputRef.current.click(); } }}
                                  disabled={uploadingDoc === s.id}
                                  className={`text-[8px] border border-dashed px-2 py-0.5 rounded transition-colors ${s.status === 'active' && !s.is_legacy ? 'text-amber-700 border-amber-400 hover:border-amber-600 hover:text-amber-900 font-semibold' : 'text-gray-400 hover:text-navy border-gray-300 hover:border-navy'}`}>
                                  {uploadingDoc === s.id ? 'Uploading…' : '+ Upload PDF'}
                                </button>
                              </>
                            )}
                            {docError?.contractId === s.id && (
                              <div role="alert" className="basis-full text-[9px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{docError.message}</div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <button onClick={() => openEditSow(s)} className="text-[8px] text-blue-500 hover:text-blue-700">Edit</button>
                          <button onClick={() => removeSow(s.id)} className="text-[8px] text-red-400 hover:text-red-600">Remove</button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              );
            })()}

            {detailTab === 'onboarding' && (() => {
              const ONB_STATUS = ['pending','in_progress','complete','na'];
              const STATUS_LABEL = { pending:'Pending', in_progress:'In progress', complete:'Complete', na:'N/A' };
              const STATUS_CLR = { pending:'bg-gray-100 text-gray-600', in_progress:'bg-amber-100 text-amber-700', complete:'bg-green-100 text-green-700', na:'bg-gray-100 text-gray-400' };
              const byId = Object.fromEntries(onboarding.map(a => [a.id, a]));
              const done = onboarding.filter(a => a.progress?.status === 'complete').length;
              return (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[9px] font-bold text-gray-600 uppercase tracking-wide">Onboarding Checklist</div>
                    <div className="text-[8px] text-gray-400">{done}/{onboarding.length} complete · configurable in Admin → Contractor Onboarding</div>
                  </div>
                  {onboarding.length === 0 ? (
                    <div className="text-[9px] text-gray-400 italic py-6 text-center">No onboarding activities defined. Add them in Admin → Contractor Onboarding.</div>
                  ) : (
                    <div className="space-y-1.5">
                      {onboarding.map(a => {
                        const p = a.progress || {};
                        const depDone = !a.depends_on || byId[a.depends_on]?.progress?.status === 'complete';
                        return (
                          <div key={a.id} className="border border-gray-100 rounded p-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[10px] font-semibold text-gray-800">{a.name}</span>
                              {a.category && <span className="text-[7px] uppercase bg-gray-100 text-gray-500 px-1 py-0.5 rounded">{a.category}</span>}
                              {a.depends_on && !depDone && <span className="text-[7px] text-amber-600" title={`Requires ${a.depends_on_name || byId[a.depends_on]?.name} first`}>⚠ needs {byId[a.depends_on]?.name || a.depends_on_name} first</span>}
                              <span className={`ml-auto text-[7px] font-bold px-1.5 py-0.5 rounded ${STATUS_CLR[p.status || 'pending']}`}>{STATUS_LABEL[p.status || 'pending']}</span>
                            </div>
                            {a.description && <div className="text-[8px] text-gray-400 mt-0.5">{a.description}</div>}
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                              <select className="form-input text-[9px] py-0.5" value={p.status || 'pending'}
                                onChange={e => setOnboardingActivity(a.id, { status: e.target.value, target_date: p.target_date || null, notes: p.notes || null })}>
                                {ONB_STATUS.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                              </select>
                              <label className="text-[8px] text-gray-400">Target
                                <input type="date" className="form-input text-[9px] py-0.5 ml-1" value={p.target_date || ''}
                                  onChange={e => setOnboardingActivity(a.id, { status: p.status || 'pending', target_date: e.target.value || null, notes: p.notes || null })} />
                              </label>
                              {p.completed_at && <span className="text-[8px] text-green-600">✓ {formatDate(p.completed_at)}</span>}
                            </div>
                            <input className="form-input text-[9px] py-0.5 mt-1" placeholder="Notes…" defaultValue={p.notes || ''}
                              onBlur={e => { if (e.target.value !== (p.notes || '')) setOnboardingActivity(a.id, { status: p.status || 'pending', target_date: p.target_date || null, notes: e.target.value || null }); }} />
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}

            {detailTab === 'timesheets' && (
              <div>
                {timesheets.length === 0 && <p className="text-[10px] text-gray-400">No timesheets.</p>}
                {timesheets.map(ts => {
                  const totalH = ts.total_hours || 0;
                  const fmtTs = (iso) => iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
                  // Parse per-entry data for per-contract breakdown
                  const DAY_KEYS = ['mon','tue','wed','thu','fri','sat','sun'];
                  const DAY_LABELS_SHORT = ['M','T','W','T','F','S','S'];
                  let contractRows = [];
                  if (ts.entries_json) {
                    try {
                      const entries = JSON.parse(ts.entries_json);
                      const byContract = {};
                      for (const e of entries) {
                        const key = e.ref || '(no ref)';
                        if (!byContract[key]) byContract[key] = { ref: e.ref, sow: e.sow, days: {} };
                        byContract[key].days[e.day] = (byContract[key].days[e.day] || 0) + e.days;
                      }
                      contractRows = Object.values(byContract);
                    } catch { /* fall through to simple display */ }
                  }
                  return (
                    <div key={ts.id} className="border border-gray-200 rounded p-3 mb-2">
                      <div className="flex items-center justify-between mb-1">
                        <div>
                          <span className="text-[11px] font-bold text-navy">Week of {formatDate(ts.week_start)}</span>
                          <span className="text-[9px] text-gray-400 ml-2">{(totalH/8).toFixed(2)}d</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-[8px] px-2 py-0.5 rounded font-semibold ${TS_BADGE[ts.status]}`}>{ts.status}</span>
                          {ts.status === 'submitted' && (
                            <>
                              <button onClick={() => approveTs(ts.id, 'approved')} disabled={approvingTs === ts.id} className="text-[8px] bg-green-600 text-white px-2 py-0.5 rounded">Approve</button>
                              <button onClick={() => approveTs(ts.id, 'rejected')} disabled={approvingTs === ts.id} className="text-[8px] bg-red-500 text-white px-2 py-0.5 rounded">Reject</button>
                            </>
                          )}
                        </div>
                      </div>
                      {ts.status === 'submitted' && (
                        <div className="text-[8px] text-blue-600 mb-2">
                          Submitted {fmtTs(ts.submitted_at)}{ts.pending_approver_name ? ` · awaiting ${ts.pending_approver_name}` : ''}
                        </div>
                      )}
                      {ts.status === 'approved' && ts.approved_by_name && (
                        <div className="text-[8px] text-green-600 mb-1">Approved by {ts.approved_by_name}</div>
                      )}
                      {/* Per-contract per-day breakdown */}
                      {contractRows.length > 0 ? (
                        <div className="mt-1 rounded border border-gray-100 overflow-x-auto">
                          <table className="w-full text-[8px]">
                            <thead>
                              <tr className="bg-gray-50 border-b border-gray-100">
                                <th className="text-left px-2 py-1 font-semibold text-gray-500 w-32">Contract / SOW</th>
                                {DAY_LABELS_SHORT.map((l, i) => (
                                  <th key={i} className="text-center px-1 py-1 font-semibold text-gray-400 w-7">{l}</th>
                                ))}
                                <th className="text-right px-2 py-1 font-semibold text-gray-500 w-10">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {contractRows.map((cr, ri) => {
                                const rowTotal = DAY_KEYS.reduce((s, d) => s + (cr.days[d] || 0), 0);
                                return (
                                  <tr key={ri} className="border-b border-gray-50 last:border-0">
                                    <td className="px-2 py-1">
                                      {cr.ref && <div className="font-bold text-navy font-mono text-[8px]">{cr.ref}</div>}
                                      {cr.sow && <div className="text-gray-400 text-[7px]">{cr.sow}</div>}
                                      {!cr.ref && <div className="text-gray-400 italic">—</div>}
                                    </td>
                                    {DAY_KEYS.map(d => {
                                      const v = cr.days[d] || 0;
                                      return (
                                        <td key={d} className={`text-center px-1 py-1 font-semibold ${v > 0 ? 'text-navy' : 'text-gray-200'}`}>
                                          {v > 0 ? v : '·'}
                                        </td>
                                      );
                                    })}
                                    <td className={`text-right px-2 py-1 font-bold ${rowTotal > 0 ? 'text-navy' : 'text-gray-300'}`}>
                                      {rowTotal > 0 ? `${rowTotal}d` : '—'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="flex gap-2 mt-1">
                          {DAY_KEYS.map((d, i) => (
                            <div key={d} className="text-center">
                              <div className="text-[7px] text-gray-400">{DAY_LABELS_SHORT[i]}</div>
                              <div className={`text-[10px] font-semibold ${(ts[`${d}_hours`]||0)>0?'text-navy':'text-gray-400'}`}>{((ts[`${d}_hours`]||0)/8)}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      {ts.notes && <div className="text-[9px] text-gray-400 mt-1">{ts.notes}</div>}
                      {ts.rejection_reason && <div className="text-[9px] text-red-500 mt-1">⚠ {ts.rejection_reason}</div>}
                    </div>
                  );
                })}
              </div>
            )}

            {detailTab === 'access-log' && (
              <div>
                {accessLog.length === 0 && <p className="text-[10px] text-gray-400">No access history.</p>}
                <div className="overflow-x-auto"><table className="w-full text-[9px]">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-1 text-gray-400 font-semibold">Event</th>
                      <th className="text-left py-1 text-gray-400 font-semibold">IP</th>
                      <th className="text-left py-1 text-gray-400 font-semibold">Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accessLog.map(log => (
                      <tr key={log.id} className="border-b border-gray-50">
                        <td className={`py-1 font-semibold ${log.event_type==='login'?'text-green-600':(log.event_type==='failed_login'||log.event_type==='locked_out'||log.event_type==='blocked_login')?'text-red-500':log.event_type==='password_reset'?'text-amber-600':log.event_type==='password_changed'?'text-blue-600':'text-gray-500'}`}>{log.event_type.replace(/_/g,' ')}</td>
                        <td className="py-1 text-gray-400 font-mono">{log.ip_address || '—'}</td>
                        <td className="py-1 text-gray-400">{fmtDT(log.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table></div>
              </div>
            )}

            {detailTab === 'comments' && (
              <div className="space-y-3">
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[9px] text-amber-800">
                  🔒 Private to HR &amp; Admin. These notes are <strong>not</strong> visible to the resource or the client.
                </div>
                <div className="border border-gray-200 rounded p-2 space-y-2">
                  <textarea className="form-input w-full text-[10px]" rows={2} placeholder="Add a private note about this resource…"
                    value={commentForm.body} onChange={e => setCommentForm(f => ({ ...f, body: e.target.value }))} />
                  <div className="flex items-center gap-2">
                    <select className="form-input text-[9px] flex-1" value={commentForm.contract_id} onChange={e => setCommentForm(f => ({ ...f, contract_id: e.target.value }))}>
                      <option value="">Tag a contract (optional)…</option>
                      {workerSows.map(c => (
                        <option key={c.id} value={c.id}>{c.reference || c.sow_reference || 'Contract'}{c.role_description ? ` — ${c.role_description}` : ''}</option>
                      ))}
                    </select>
                    <button onClick={addComment} disabled={commentBusy || !commentForm.body.trim()}
                      className="px-3 py-1.5 text-[9px] font-semibold rounded bg-navy text-white hover:bg-navy/90 disabled:opacity-50 whitespace-nowrap">Add note</button>
                  </div>
                </div>
                {comments.length === 0 ? (
                  <p className="text-[10px] text-gray-400">No notes yet.</p>
                ) : comments.map(c => (
                  <div key={c.id} className="border border-gray-100 rounded p-2 group">
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-[10px] text-gray-800 whitespace-pre-wrap flex-1">{c.body}</div>
                      <button onClick={() => deleteComment(c.id)} className="text-[9px] text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100">Del</button>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-[8px] text-gray-400 flex-wrap">
                      <span>{c.created_by_name || 'Unknown'}</span>
                      <span>·</span>
                      <span>{fmtDT(c.created_at)}</span>
                      {(c.contract_reference || c.contract_role) && (
                        <span className="bg-navy/10 text-navy px-1.5 py-0.5 rounded-full font-semibold">{c.contract_reference || c.contract_role}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>

      {/* Edit Modal */}
      {showEdit && (
        <Modal title="Edit Resource Details" onClose={() => setShowEdit(false)}>
            {editError &&<div className="text-[9px] text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 mb-3">{editError}</div>}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">First Name *</label>
                  <input className="form-input" value={editForm.first_name} onChange={e => setEditForm(f => ({...f, first_name: e.target.value}))} autoFocus />
                </div>
                <div>
                  <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Last Name *</label>
                  <input className="form-input" value={editForm.last_name} onChange={e => setEditForm(f => ({...f, last_name: e.target.value}))} />
                </div>
              </div>
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Email (login)</label>
                <input className="form-input" type="email" value={editForm.email} onChange={e => setEditForm(f => ({...f, email: e.target.value}))} />
                <p className="text-[7px] text-gray-400 mt-0.5">Changing email updates their portal login.</p>
              </div>
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Phone</label>
                <input className="form-input" value={editForm.phone} onChange={e => setEditForm(f => ({...f, phone: e.target.value}))} />
              </div>
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Notes</label>
                <textarea className="form-input" rows={2} value={editForm.notes} onChange={e => setEditForm(f => ({...f, notes: e.target.value}))} />
              </div>
              {detail?.supplier_id && (
                <div className="border-t border-gray-100 pt-3 mt-1">
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-[8px] font-bold text-gray-400 uppercase">Supplier Company</div>
                    <button type="button" onClick={openEditSupplier}
                      className="text-[8px] text-blue-600 hover:underline font-medium">Edit supplier details →</button>
                  </div>
                  <div className="text-[10px] font-semibold text-navy">{detail.supplier_name || '—'}</div>
                </div>
              )}
              <div className="border border-amber-200 rounded-lg p-3 bg-amber-50">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" className="mt-0.5" checked={!!editForm.emails_enabled} onChange={e => setEditForm(f => ({...f, emails_enabled: e.target.checked}))} />
                  <div>
                    <div className="text-[9px] font-semibold text-amber-800">Automated emails enabled</div>
                    <div className="text-[8px] text-amber-600 mt-0.5">Uncheck to suppress all automated timesheet emails (submission notices to approvers, decisions back to contractor). Use while loading historical data.</div>
                  </div>
                </label>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowEdit(false)} className="flex-1 btn-secondary text-[10px]">Cancel</button>
              <button onClick={saveEdit} disabled={editing} className="flex-1 btn-primary text-[10px] disabled:opacity-40">{editing ? 'Saving…' : 'Save Changes'}</button>
            </div>
        </Modal>
      )}

      {/* Send Welcome Email Modal */}
      {showWelcome && detail && (
        <Modal title="Send Welcome Email" onClose={() => { if (!sendingWelcome) setShowWelcome(false); }}>
            <p className="text-[10px] text-gray-600 mb-2">
              This will email <span className="font-semibold">{detail.first_name} {detail.last_name}</span> at{' '}
              <span className="font-semibold">{detail.email}</span> using the branded template, with their portal link and login details.
            </p>
            <ul className="text-[9px] text-gray-500 list-disc pl-4 mb-3 space-y-0.5">
              <li>A new <strong>temporary password</strong> is generated and set.</li>
              <li>They&apos;ll be <strong>required to change it</strong> on first login.</li>
              <li>Existing sessions are signed out and any lockout is cleared.</li>
              <li>The reset is recorded in the access log.</li>
            </ul>
            {!detail.is_active && (
              <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[9px] text-amber-800 mb-3">
                ⚠ This resource is currently inactive — they won&apos;t be able to sign in until reactivated.
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={() => setShowWelcome(false)} disabled={sendingWelcome} className="flex-1 btn-secondary text-[10px]">Cancel</button>
              <button onClick={sendWelcome} disabled={sendingWelcome} className="flex-1 btn-primary text-[10px]">
                {sendingWelcome ? 'Sending…' : 'Send Welcome Email'}
              </button>
            </div>
        </Modal>
      )}

      {/* Password Reset Modal */}
      {showPwReset && (
        <Modal title="Reset Password" onClose={() => setShowPwReset(false)}>
            <p className="text-[9px] text-gray-500 mb-3">All existing sessions will be invalidated.</p>
            <label className="block text-[9px] font-semibold text-gray-500 mb-1">New Password</label>
            <input type="password" className="form-input mb-4" value={newPw} onChange={e => setNewPw(e.target.value)} />
            <div className="flex gap-2">
              <button onClick={() => setShowPwReset(false)} className="flex-1 btn-secondary text-[10px]">Cancel</button>
              <button onClick={resetPassword} className="flex-1 btn-primary text-[10px]">Reset</button>
            </div>
        </Modal>
      )}

      {/* Delete Confirmation Modal */}
      {showDelete && detail && (
        <Modal title={<span className="text-red-700">Permanently Delete Resource</span>} onClose={() => { if (!deleting) setShowDelete(false); }}>
            <p className="text-[10px] text-gray-600 mb-1">You are about to permanently delete:</p>
            <p className="text-[11px] font-bold text-navy mb-3">{detail.first_name} {detail.last_name} ({detail.email})</p>
            <p className="text-[9px] text-gray-500 mb-4">This will remove all their access logs, contracts, timesheets and invoices. This cannot be undone.</p>
            <div className="flex gap-2">
              <button onClick={() => setShowDelete(false)} className="flex-1 btn-secondary text-[10px]">Cancel</button>
              <button onClick={deleteWorker} disabled={deleting}
                className="flex-1 px-4 py-2 text-[10px] font-bold rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-40">
                {deleting ? 'Deleting…' : 'Yes, Delete Permanently'}
              </button>
            </div>
        </Modal>
      )}

      {/* Add Contract Modal */}
      {showAddSow && (
        <Modal
          title="New Resource Contract"
          onClose={() => { setShowAddSow(false); setSowError(''); setSowInfo(null); }}
          footer={
            <>
              <button onClick={() => { setShowAddSow(false); setSowError(''); setSowInfo(null); }} className="btn-secondary text-[10px]">Cancel</button>
              <button onClick={addSow} className="px-4 py-2 text-[10px] font-bold rounded-lg bg-navy text-white hover:bg-navy/90 transition-colors">Create Contract</button>
            </>
          }
        >
            <p className="text-[9px] text-gray-400 mb-4">Contract issued to the supplier resource — link to a SOW for billable work, or choose an overhead category.</p>
            {sowError && <div className="text-[9px] text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 mb-3">{sowError}</div>}
            <div className="space-y-3">
              <div>
                <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">Contract Reference <span className="font-normal normal-case text-gray-400">(optional — auto-generated as ATV_PS_XXXX if blank)</span></label>
                <input className="form-input font-mono" placeholder="e.g. ATV_PS_1503 or leave blank to auto-generate" value={sowForm.reference} onChange={e => setSowForm(f => ({...f,reference:e.target.value}))} />
              </div>
              <div>
                <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">SOW (billable) — leave blank for overhead</label>
                <SowSelect sows={sows} value={sowForm.sow_id} onChange={id => onSelectSow(id)} placeholder="— no SOW (overhead / internal) —" />
                {sowInfo && sowInfo.existing_contracts && sowInfo.existing_contracts.length > 0 && (
                  <div className="mt-1 text-[8px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    ⚠ This SOW already backs {sowInfo.existing_contracts.length} contract{sowInfo.existing_contracts.length !== 1 ? 's' : ''}
                    {' '}({sowInfo.existing_contracts.map(c => `${c.reference}${c.worker_name ? ' · ' + c.worker_name : ''}`).join(', ')}).
                    A SOW should map 1:1 to a single contract — check before creating another.
                  </div>
                )}
                {sowInfo && sowInfo.default_days != null && sowForm.agreed_days && parseFloat(sowForm.agreed_days) !== parseFloat(sowInfo.default_days) && (
                  <div className="mt-1 text-[8px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    ⚠ Agreed days ({sowForm.agreed_days}) don't match the SOW's {sowInfo.default_days} days. The contract should normally match the SOW.
                  </div>
                )}
              </div>
              {!sowForm.sow_id && (
                <div>
                  <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">Overhead Category *</label>
                  <select className="form-input" value={sowForm.overhead_category} onChange={e => setSowForm(f => ({...f,overhead_category:e.target.value}))}>
                    <option value="">— select category —</option>
                    <option value="bench">Bench</option>
                    <option value="internal_project">Internal Project</option>
                    <option value="training">Training</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">Status</label>
                  <select className="form-input" value={sowForm.status} onChange={e => setSowForm(f => ({...f,status:e.target.value}))}>
                    <option value="draft">Draft</option>
                    <option value="issued">Issued</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">Role</label>
                  <input className="form-input" placeholder="e.g. Lead Developer" value={sowForm.role_description} onChange={e => setSowForm(f => ({...f,role_description:e.target.value}))} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">Day Rate £ (we pay)</label>
                  <input className="form-input" type="number" min="0" step="0.01" placeholder="e.g. 450" value={sowForm.day_rate} onChange={e => setSowForm(f => ({...f,day_rate:e.target.value}))} />
                </div>
                <div>
                  <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">Agreed Days</label>
                  <input className="form-input" type="number" min="0" step="0.5" placeholder="e.g. 20" value={sowForm.agreed_days} onChange={e => setSowForm(f => ({...f,agreed_days:e.target.value}))} />
                </div>
                <div>
                  <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">Expense Budget £</label>
                  <input className="form-input" type="number" min="0" step="1" placeholder="e.g. 500" value={sowForm.agreed_expenses} onChange={e => setSowForm(f => ({...f,agreed_expenses:e.target.value}))} />
                </div>
              </div>
              {sowForm.day_rate && sowForm.agreed_days && (
                <div className="text-[9px] text-gray-500 bg-gray-50 rounded px-2 py-1">
                  Total cost: <span className="font-semibold text-navy">£{(parseFloat(sowForm.day_rate) * parseFloat(sowForm.agreed_days)).toLocaleString()}</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">Start Date</label>
                  <input className="form-input" type="date" value={sowForm.start_date} onChange={e => setSowForm(f => ({...f,start_date:e.target.value}))} />
                </div>
                <div>
                  <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">End Date</label>
                  <input className="form-input" type="date" value={sowForm.end_date} onChange={e => setSowForm(f => ({...f,end_date:e.target.value}))} />
                </div>
              </div>

              {/* Timesheet Approver */}
              <div className="border border-gray-200 rounded-lg p-3 space-y-2">
                <div className="text-[9px] font-semibold text-gray-500 uppercase">Timesheet Approver</div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setSowForm(f => ({...f,timesheet_approver_type:'external',timesheet_approver_id:sowInfo?.default_approver_id||''}))}
                    className={`flex-1 text-[9px] py-1 rounded border font-semibold ${sowForm.timesheet_approver_type==='external'?'bg-navy text-white border-navy':'bg-white text-gray-600 border-gray-200'}`}>
                    Client Contact
                  </button>
                  <button type="button" onClick={() => setSowForm(f => ({...f,timesheet_approver_type:'internal',timesheet_approver_id:''}))}
                    className={`flex-1 text-[9px] py-1 rounded border font-semibold ${sowForm.timesheet_approver_type==='internal'?'bg-navy text-white border-navy':'bg-white text-gray-600 border-gray-200'}`}>
                    Internal (ATV)
                  </button>
                </div>
                {sowForm.timesheet_approver_type === 'external' && (
                  <select className="form-input text-[10px]" value={sowForm.timesheet_approver_id} onChange={e => setSowForm(f => ({...f,timesheet_approver_id:e.target.value}))}>
                    <option value="">— select client contact —</option>
                    {(sowInfo?.client_contacts || []).map(cc => (
                      <option key={cc.id} value={cc.id}>{cc.name}{cc.email ? ` (${cc.email})` : ''}</option>
                    ))}
                  </select>
                )}
                {sowForm.timesheet_approver_type === 'internal' && (
                  <select className="form-input text-[10px]" value={sowForm.timesheet_approver_id} onChange={e => setSowForm(f => ({...f,timesheet_approver_id:e.target.value}))}>
                    <option value="">— select ATV person —</option>
                    {internalPeople.map(p => (
                      <option key={p.id} value={p.id}>{p.first_name} {p.last_name} ({p.email_work})</option>
                    ))}
                  </select>
                )}
                {!sowInfo && sowForm.sow_id === '' && sowForm.timesheet_approver_type === 'external' && (
                  <div className="text-[8px] text-amber-600">Select a SOW first to load available client contacts.</div>
                )}
              </div>

              {/* Delegate (backup) Approver */}
              <div className="border border-gray-200 rounded-lg p-3 space-y-2">
                <div className="text-[9px] font-semibold text-gray-500 uppercase">Delegate Approver <span className="text-gray-300 normal-case font-normal">— optional backup</span></div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setSowForm(f => ({...f,delegate_approver_type:'external',delegate_approver_id:''}))}
                    className={`flex-1 text-[9px] py-1 rounded border font-semibold ${sowForm.delegate_approver_type==='external'?'bg-navy text-white border-navy':'bg-white text-gray-600 border-gray-200'}`}>
                    Client Contact
                  </button>
                  <button type="button" onClick={() => setSowForm(f => ({...f,delegate_approver_type:'internal',delegate_approver_id:''}))}
                    className={`flex-1 text-[9px] py-1 rounded border font-semibold ${sowForm.delegate_approver_type==='internal'?'bg-navy text-white border-navy':'bg-white text-gray-600 border-gray-200'}`}>
                    Internal (ATV)
                  </button>
                </div>
                {sowForm.delegate_approver_type === 'external' && (
                  <select className="form-input text-[10px]" value={sowForm.delegate_approver_id} onChange={e => setSowForm(f => ({...f,delegate_approver_id:e.target.value}))}>
                    <option value="">— no delegate —</option>
                    {(sowInfo?.client_contacts || []).map(cc => (
                      <option key={cc.id} value={cc.id}>{cc.name}{cc.email ? ` (${cc.email})` : ''}</option>
                    ))}
                  </select>
                )}
                {sowForm.delegate_approver_type === 'internal' && (
                  <select className="form-input text-[10px]" value={sowForm.delegate_approver_id} onChange={e => setSowForm(f => ({...f,delegate_approver_id:e.target.value}))}>
                    <option value="">— no delegate —</option>
                    {internalPeople.map(p => (
                      <option key={p.id} value={p.id}>{p.first_name} {p.last_name} ({p.email_work})</option>
                    ))}
                  </select>
                )}
                <div className="text-[8px] text-gray-400">Authorised to approve timesheets when the primary approver is unavailable.</div>
              </div>

              <div>
                <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">Notes</label>
                <textarea className="form-input" rows={2} value={sowForm.notes} onChange={e => setSowForm(f => ({...f,notes:e.target.value}))} />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={sowForm.is_primary} onChange={e => setSowForm(f => ({...f,is_primary:e.target.checked}))} />
                <span className="text-[9px] text-gray-600">Primary contract (default for timesheets)</span>
              </label>
            </div>
        </Modal>
      )}

      {/* Edit Contract Modal */}
      {editingSow && (
        <Modal
          title={<>Edit Contract <span className="font-mono text-[10px] text-gray-400">{editingSow.reference}</span></>}
          onClose={() => setEditingSow(null)}
          footer={
            <>
              <button onClick={() => setEditingSow(null)} className="btn-secondary text-[10px]">Cancel</button>
              <button onClick={saveEditSow} className="px-4 py-2 text-[10px] font-bold rounded-lg bg-navy text-white hover:bg-navy/90 transition-colors">Save Changes</button>
            </>
          }
        >
            {editSowError &&<div className="text-[9px] text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 mb-3">{editSowError}</div>}
            <div className="space-y-3">
              <div>
                <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">SOW (billable) — leave blank for overhead</label>
                <SowSelect sows={sows} value={editSowForm.sow_id} onChange={id => setEditSowForm(f => ({...f,sow_id:id,overhead_category:id?'':f.overhead_category}))} placeholder="— no SOW (overhead / internal) —" />
              </div>
              {!editSowForm.sow_id && (
                <div>
                  <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">Overhead Category</label>
                  <select className="form-input" value={editSowForm.overhead_category} onChange={e => setEditSowForm(f => ({...f,overhead_category:e.target.value}))}>
                    <option value="">— select category —</option>
                    <option value="bench">Bench</option>
                    <option value="internal_project">Internal Project</option>
                    <option value="training">Training</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">Status</label>
                  <select className="form-input" value={editSowForm.status} onChange={e => setEditSowForm(f => ({...f,status:e.target.value}))}>
                    <option value="draft">Draft</option>
                    <option value="issued">Issued</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">Role</label>
                  <input className="form-input" placeholder="e.g. Lead Developer" value={editSowForm.role_description} onChange={e => setEditSowForm(f => ({...f,role_description:e.target.value}))} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">Day Rate £ (we pay)</label>
                  <input className="form-input" type="number" min="0" step="0.01" placeholder="e.g. 450" value={editSowForm.day_rate} onChange={e => setEditSowForm(f => ({...f,day_rate:e.target.value}))} />
                </div>
                <div>
                  <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">Agreed Days</label>
                  <input className="form-input" type="number" min="0" step="0.5" placeholder="e.g. 20" value={editSowForm.agreed_days} onChange={e => setEditSowForm(f => ({...f,agreed_days:e.target.value}))} />
                </div>
                <div>
                  <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">Expense Budget £</label>
                  <input className="form-input" type="number" min="0" step="1" placeholder="e.g. 500" value={editSowForm.agreed_expenses} onChange={e => setEditSowForm(f => ({...f,agreed_expenses:e.target.value}))} />
                </div>
              </div>
              {editSowForm.day_rate && editSowForm.agreed_days && (
                <div className="text-[9px] text-gray-500 bg-gray-50 rounded px-2 py-1">
                  Total cost: <span className="font-semibold text-navy">£{(parseFloat(editSowForm.day_rate) * parseFloat(editSowForm.agreed_days)).toLocaleString()}</span>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">Start Date</label>
                  <input className="form-input" type="date" value={editSowForm.start_date} onChange={e => setEditSowForm(f => ({...f,start_date:e.target.value}))} />
                </div>
                <div>
                  <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">End Date</label>
                  <input className="form-input" type="date" value={editSowForm.end_date} onChange={e => setEditSowForm(f => ({...f,end_date:e.target.value}))} />
                </div>
              </div>

              {/* Timesheet Approver */}
              <div className="border border-gray-200 rounded-lg p-3 space-y-2">
                <div className="text-[9px] font-semibold text-gray-500 uppercase">Timesheet Approver</div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setEditSowForm(f => ({...f,timesheet_approver_type:'external'}))}
                    className={`flex-1 text-[9px] py-1 rounded border font-semibold ${editSowForm.timesheet_approver_type==='external'?'bg-navy text-white border-navy':'bg-white text-gray-600 border-gray-200'}`}>
                    Client Contact
                  </button>
                  <button type="button" onClick={() => setEditSowForm(f => ({...f,timesheet_approver_type:'internal'}))}
                    className={`flex-1 text-[9px] py-1 rounded border font-semibold ${editSowForm.timesheet_approver_type==='internal'?'bg-navy text-white border-navy':'bg-white text-gray-600 border-gray-200'}`}>
                    Internal (ATV)
                  </button>
                </div>
                {editSowForm.timesheet_approver_type === 'external' && (editSowForm.sow_id || editingSow?.sow_id) && (
                  <ClientContactDropdown sowId={editSowForm.sow_id || editingSow?.sow_id} value={editSowForm.timesheet_approver_id}
                    onChange={v => setEditSowForm(f => ({...f,timesheet_approver_id:v}))} />
                )}
                {editSowForm.timesheet_approver_type === 'external' && !editSowForm.sow_id && !editingSow?.sow_id && (
                  <div className="text-[8px] text-amber-600">Select a SOW above to load available client contacts.</div>
                )}
                {editSowForm.timesheet_approver_type === 'internal' && (
                  <select className="form-input text-[10px]" value={editSowForm.timesheet_approver_id} onChange={e => setEditSowForm(f => ({...f,timesheet_approver_id:e.target.value}))}>
                    <option value="">— select ATV person —</option>
                    {internalPeople.map(p => (
                      <option key={p.id} value={p.id}>{p.first_name} {p.last_name} ({p.email_work})</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Delegate (backup) Approver */}
              <div className="border border-gray-200 rounded-lg p-3 space-y-2">
                <div className="text-[9px] font-semibold text-gray-500 uppercase">Delegate Approver <span className="text-gray-300 normal-case font-normal">— optional backup</span></div>
                <div className="flex gap-2">
                  <button type="button" onClick={() => setEditSowForm(f => ({...f,delegate_approver_type:'external'}))}
                    className={`flex-1 text-[9px] py-1 rounded border font-semibold ${editSowForm.delegate_approver_type==='external'?'bg-navy text-white border-navy':'bg-white text-gray-600 border-gray-200'}`}>
                    Client Contact
                  </button>
                  <button type="button" onClick={() => setEditSowForm(f => ({...f,delegate_approver_type:'internal'}))}
                    className={`flex-1 text-[9px] py-1 rounded border font-semibold ${editSowForm.delegate_approver_type==='internal'?'bg-navy text-white border-navy':'bg-white text-gray-600 border-gray-200'}`}>
                    Internal (ATV)
                  </button>
                </div>
                {editSowForm.delegate_approver_type === 'external' && (editSowForm.sow_id || editingSow?.sow_id) && (
                  <ClientContactDropdown sowId={editSowForm.sow_id || editingSow?.sow_id} value={editSowForm.delegate_approver_id}
                    onChange={v => setEditSowForm(f => ({...f,delegate_approver_id:v}))} placeholder="— no delegate —" />
                )}
                {editSowForm.delegate_approver_type === 'external' && !editSowForm.sow_id && !editingSow?.sow_id && (
                  <div className="text-[8px] text-amber-600">Select a SOW above to load available client contacts.</div>
                )}
                {editSowForm.delegate_approver_type === 'internal' && (
                  <select className="form-input text-[10px]" value={editSowForm.delegate_approver_id} onChange={e => setEditSowForm(f => ({...f,delegate_approver_id:e.target.value}))}>
                    <option value="">— no delegate —</option>
                    {internalPeople.map(p => (
                      <option key={p.id} value={p.id}>{p.first_name} {p.last_name} ({p.email_work})</option>
                    ))}
                  </select>
                )}
                <div className="text-[8px] text-gray-400">Authorised to approve timesheets when the primary approver is unavailable.</div>
              </div>

              {/* Historical opening balance */}
              <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 space-y-2">
                <div className="text-[9px] font-semibold text-amber-700 uppercase">Historical Opening Balance (Migration)</div>
                <div className="text-[8px] text-amber-600">Pre-system days and expenses consumed before go-live. These count toward drawdown without creating timesheets or invoices.</div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">Opening Days</label>
                    <input className="form-input" type="number" min="0" step="0.5" placeholder="e.g. 10" value={editSowForm.opening_days} onChange={e => setEditSowForm(f => ({...f,opening_days:e.target.value}))} />
                  </div>
                  <div>
                    <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">Opening Expenses £</label>
                    <input className="form-input" type="number" min="0" step="1" placeholder="e.g. 250" value={editSowForm.opening_expenses} onChange={e => setEditSowForm(f => ({...f,opening_expenses:e.target.value}))} />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">Notes</label>
                <textarea className="form-input" rows={2} value={editSowForm.notes} onChange={e => setEditSowForm(f => ({...f,notes:e.target.value}))} />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editSowForm.is_primary} onChange={e => setEditSowForm(f => ({...f,is_primary:e.target.checked}))} />
                <span className="text-[9px] text-gray-600">Primary contract</span>
              </label>
              <div className="border border-amber-200 bg-amber-50 rounded-lg p-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" className="mt-0.5" checked={!!editSowForm.is_legacy} onChange={e => setEditSowForm(f => ({...f,is_legacy:e.target.checked}))} />
                  <div>
                    <div className="text-[9px] font-semibold text-amber-800">Legacy / pre-migration contract</div>
                    <div className="text-[8px] text-amber-600 mt-0.5">Mark contracts imported from before system go-live. Legacy contracts are excluded from financial overhead calculations to prevent inflated figures.</div>
                  </div>
                </label>
              </div>
            </div>
        </Modal>
      )}

      {/* Link / Create Supplier Modal */}
      {showSupplierModal && (
        <Modal title="Link Supplier" onClose={() => setShowSupplierModal(false)}>
            <div className="space-y-3">
              {supplierMsg && (
                <div className="text-[10px] px-3 py-2 rounded bg-red-50 text-red-600 border border-red-200">{supplierMsg}</div>
              )}
              {!supplierCreateMode ? (
                <>
                  <div>
                    <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">Search existing suppliers</label>
                    <input className="form-input text-[11px]" placeholder="Type to filter…"
                      value={supplierSearch} onChange={e => setSupplierSearch(e.target.value)} autoFocus />
                  </div>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {allSuppliers
                      .filter(s => !supplierSearch || s.name.toLowerCase().includes(supplierSearch.toLowerCase()))
                      .map(s => (
                        <button key={s.id} onClick={() => linkSupplier(s.id)} disabled={supplierSaving}
                          className="w-full text-left px-3 py-2 rounded-lg border border-gray-100 hover:bg-gold/5 hover:border-gold/30 transition-colors">
                          <div className="text-[11px] font-semibold text-navy">{s.name}</div>
                          {(s.company_number || s.vat_number) && (
                            <div className="text-[9px] text-gray-400">{[s.company_number && `Co. ${s.company_number}`, s.vat_number && `VAT ${s.vat_number}`].filter(Boolean).join(' · ')}</div>
                          )}
                        </button>
                      ))}
                    {allSuppliers.filter(s => !supplierSearch || s.name.toLowerCase().includes(supplierSearch.toLowerCase())).length === 0 && (
                      <div className="text-[9px] text-gray-400 italic text-center py-3">No suppliers match</div>
                    )}
                  </div>
                  <button onClick={() => setSupplierCreateMode(true)}
                    className="w-full btn btn-secondary text-[10px]">
                    + Create new supplier
                  </button>
                </>
              ) : (
                <>
                  <div className="text-[9px] text-gray-500 bg-blue-50 border border-blue-100 rounded px-3 py-2">
                    Pre-filled from this contractor's company details. Review and save to create the supplier record.
                  </div>
                  {[
                    { key: 'name',           label: 'Supplier Name *', placeholder: 'Acme Consulting Ltd' },
                    { key: 'company_number', label: 'Company Number',  placeholder: '12345678' },
                    { key: 'vat_number',     label: 'VAT Number',      placeholder: 'GB123456789' },
                    { key: 'contact_name',   label: 'Contact Name',    placeholder: '' },
                    { key: 'contact_email',  label: 'Contact Email',   placeholder: '' },
                    { key: 'contact_phone',  label: 'Contact Phone',   placeholder: '' },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">{f.label}</label>
                      <input className="form-input text-[11px]" placeholder={f.placeholder}
                        value={supplierForm[f.key]} onChange={e => setSupplierForm(sf => ({ ...sf, [f.key]: e.target.value }))} />
                    </div>
                  ))}
                  <div>
                    <label className="block text-[9px] font-semibold text-gray-500 uppercase mb-1">Registered Address</label>
                    <textarea rows={3} className="form-input text-[11px]"
                      value={supplierForm.address} onChange={e => setSupplierForm(sf => ({ ...sf, address: e.target.value }))} />
                  </div>
                </>
              )}
            </div>
            {supplierCreateMode && (
              <div className="flex gap-2 p-4 border-t border-gray-100">
                <button onClick={() => setSupplierCreateMode(false)} className="flex-1 btn btn-secondary text-[10px]">← Back</button>
                <button onClick={createAndLinkSupplier} disabled={supplierSaving || !supplierForm.name.trim()}
                  className="flex-1 btn btn-primary text-[10px] disabled:opacity-40">
                  {supplierSaving ? 'Creating…' : 'Create & Link'}
                </button>
              </div>
            )}
        </Modal>
      )}

      {/* Edit Supplier Details Modal */}
      {showEditSupplierModal && (
        <Modal title="Edit Supplier Details" onClose={() => setShowEditSupplierModal(false)}>
            {editSupplierMsg &&<div className="text-[9px] text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2 mb-3">{editSupplierMsg}</div>}
            <div className="space-y-3">
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Supplier Name *</label>
                <input className="form-input" value={editSupplierForm.name} onChange={e => setEditSupplierForm(f => ({...f, name: e.target.value}))} autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Company Number</label>
                  <input className="form-input" placeholder="e.g. 12345678" value={editSupplierForm.company_number} onChange={e => setEditSupplierForm(f => ({...f, company_number: e.target.value}))} />
                </div>
                <div>
                  <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">VAT Number</label>
                  <input className="form-input" placeholder="e.g. GB123456789" value={editSupplierForm.vat_number} onChange={e => setEditSupplierForm(f => ({...f, vat_number: e.target.value}))} />
                </div>
              </div>
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Contact Name</label>
                <input className="form-input" value={editSupplierForm.contact_name} onChange={e => setEditSupplierForm(f => ({...f, contact_name: e.target.value}))} />
              </div>
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Contact Email</label>
                <input className="form-input" type="email" value={editSupplierForm.contact_email} onChange={e => setEditSupplierForm(f => ({...f, contact_email: e.target.value}))} />
              </div>
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Contact Phone</label>
                <input className="form-input" value={editSupplierForm.contact_phone} onChange={e => setEditSupplierForm(f => ({...f, contact_phone: e.target.value}))} />
              </div>
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Registered Address</label>
                <textarea className="form-input" rows={2} value={editSupplierForm.address} onChange={e => setEditSupplierForm(f => ({...f, address: e.target.value}))} />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setShowEditSupplierModal(false)} className="flex-1 btn btn-secondary text-[10px]">Cancel</button>
              <button onClick={saveEditSupplier} disabled={editSupplierSaving || !editSupplierForm.name?.trim()}
                className="flex-1 btn btn-primary text-[10px] disabled:opacity-40">{editSupplierSaving ? 'Saving…' : 'Save Changes'}</button>
            </div>
        </Modal>
      )}
    </>
  );
}
